import React, { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckCircleIcon, XMarkIcon } from './icons';
import { cn } from '../utils/cn';

export interface Option {
    value: string | number;
    label: string;
    description?: string; // Optional subtitle
    icon?: React.ReactNode; // Optional icon for the item
}

export interface GroupedOption {
    label: string;
    options: Option[];
}

interface CustomSelectProps {
    value: string | number | string[];
    onChange: (value: string | string[]) => void;
    isMulti?: boolean;
    options?: Option[];
    groups?: GroupedOption[];
    label?: string; // Top label (e.g. "STATUS")
    placeholder?: string;
    icon?: React.ReactNode; // Left icon in the trigger
    className?: string; // Extra classes for the container
    disabled?: boolean;
    required?: boolean;
    error?: string; // Validation error message
    selectedPrefix?: string; // Optional prefix when items are selected (e.g. "Status: 4 ausgewählt"), Doppelpunkt aus der Übersetzung
    id?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    value,
    onChange,
    isMulti = false,
    options = [],
    groups = [],
    label,
    placeholder,
    icon,
    className = "",
    disabled = false,
    required = false,
    error,
    selectedPrefix,
    id
}) => {
    const { t } = useTranslation();
    const placeholderText = placeholder ?? t('components.selectPlaceholder');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [dropdownPos, setDropdownPos] = useState<{ top?: number, bottom?: number, left: number, width: number }>({ top: 0, left: 0, width: 0 });
    const [isMobile, setIsMobile] = useState(false);
    
    const generatedId = useId();
    const resolvedId = id || generatedId;
    const valueId = `${resolvedId}-value`;

    // Zugänglicher Name = Beschriftung + gewählter Wert. Ein <label htmlFor> (eigenes oder vom Aufrufer)
    // ersetzt sonst den Inhalt des Knopfs, und Screenreader hören den gewählten Wert nicht mehr.
    useLayoutEffect(() => {
        const trigger = triggerRef.current;
        const labelEl = trigger?.labels?.[0];
        if (!trigger) return;
        if (!labelEl) {
            trigger.removeAttribute('aria-labelledby');
            return;
        }
        if (!labelEl.id) labelEl.id = `${resolvedId}-label`;
        trigger.setAttribute('aria-labelledby', `${labelEl.id} ${valueId}`);
    });

    // Focus Management when opening
    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            // Focus the first button (option) after a short delay to ensure portal is rendered
            setTimeout(() => {
                if (dropdownRef.current) {
                    const firstBtn = dropdownRef.current.querySelector('button');
                    if (firstBtn) firstBtn.focus();
                }
            }, 50);
        }
    }, [isOpen]);

    // Flatten options to find selected label easily
    const allOptions = [
        ...options,
        ...groups.flatMap(g => g.options)
    ];

    const valueArray = Array.isArray(value) ? value.map(String) : [String(value)];
    const selectedOptions = allOptions.filter(o => valueArray.includes(String(o.value)));
    const selectedOption = !isMulti ? selectedOptions[0] : undefined;

    useLayoutEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsMobile(window.innerWidth < 1024);
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate position when opening (Desktop)
    useLayoutEffect(() => {
        if (isOpen && containerRef.current && !isMobile) {
            const rect = containerRef.current.getBoundingClientRect();
            const height = 250; // Approximated max height of dropdown
            const viewportHeight = window.innerHeight;

            // Default: Drop Down
            let isFlipped = false;

            // Flip Logic: Only flip if it goes off screen AND there is space above
            if (rect.bottom + height > viewportHeight && rect.top > height) {
                isFlipped = true;
            }

            setDropdownPos({
                top: isFlipped ? undefined : rect.bottom + 4,
                bottom: isFlipped ? viewportHeight - rect.top + 4 : undefined,
                left: rect.left,
                width: rect.width
            });
        }
    }, [isOpen, isMobile]);

    // Update position on scroll/resize to prevent floating (Desktop)
    useEffect(() => {
        if (isMobile) return;

        const handleScrollOrResize = (e: Event) => {
            if (e.type === 'scroll' && dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
                return; // Do not close if the scroll happened inside the dropdown (the options list)
            }
            // Recalculate position instead of closing: the page also scrolls without the user, e.g. when a
            // validation message disappears on blur; closing then swallowed the click that opened the list
            if (isOpen && containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const height = 250;
                const viewportHeight = window.innerHeight;
                let isFlipped = false;
                if (rect.bottom + height > viewportHeight && rect.top > height) {
                    isFlipped = true;
                }
                setDropdownPos({
                    top: isFlipped ? undefined : rect.bottom + 4,
                    bottom: isFlipped ? viewportHeight - rect.top + 4 : undefined,
                    left: rect.left,
                    width: rect.width
                });
            }
        };

        if (isOpen) {
            window.addEventListener('scroll', handleScrollOrResize, true);
            window.addEventListener('resize', handleScrollOrResize);
        }
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen, isMobile]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const clickedTrigger = containerRef.current && containerRef.current.contains(event.target as Node);
            const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(event.target as Node);

            if (!clickedTrigger && !clickedDropdown) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (val: string | number) => {
        if (isMulti) {
            const strVal = String(val);
            if (valueArray.includes(strVal)) {
                onChange(valueArray.filter(v => v !== strVal));
            } else {
                onChange([...valueArray, strVal]);
            }
        } else {
            onChange(String(val));
            setIsOpen(false);
        }
    };

    return (
        <div className={cn("relative", className)} ref={containerRef}>
            {label && (
                <label htmlFor={resolvedId} className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && '*'}
                </label>
            )}

            <button
                ref={triggerRef}
                id={resolvedId}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape' && isOpen) {
                        e.stopPropagation();
                        setIsOpen(false);
                    }
                }}
                className={cn(
                    "w-full flex items-center justify-between",
                    "border border-gray-300 rounded-lg p-2.5 text-sm",
                    "bg-white text-gray-900",
                    "focus:ring-2 focus:ring-blue-500 focus:outline-none",
                    "transition-all",
                    disabled ? "opacity-50 cursor-not-allowed bg-gray-50 pointer-events-none" : "hover:border-gray-400",
                    error ? "border-red-500 bg-red-50/10" : isOpen && "ring-2 ring-blue-500 border-blue-500"
                )}
                disabled={disabled}
            >
                <div className="flex items-center gap-2 truncate">
                    {/* Show icon from prop OR from selected option if prop not provided */}
                    {icon ? (
                        <span className="text-gray-500">{icon}</span>
                    ) : selectedOption?.icon ? (
                        <span className="text-gray-500">{selectedOption.icon}</span>
                    ) : null}

                    <span id={valueId} className={cn("truncate", (!isMulti && !selectedOption) || (isMulti && selectedOptions.length === 0) ? "text-gray-500" : "")}>
                        {isMulti
                            ? (selectedOptions.length === 0 ? placeholderText : `${selectedPrefix ? `${t('components.labelColon', { label: selectedPrefix })} ` : ''}${selectedOptions.length === 1 ? selectedOptions[0].label : t('common.selectedCount', '{{count}} ausgewählt', { count: selectedOptions.length })}`)
                            : (selectedOption ? selectedOption.label : placeholderText)
                        }
                    </span>
                </div>
                <ChevronDownIcon className={cn("w-4 h-4 text-gray-400 transition-transform duration-200", isOpen && "transform rotate-180")} />
            </button>

            {/* Portal for Dropdown Options */}
            {isOpen && createPortal(
                <AnimatePresence>
                    {isMobile ? (
                        // Mobile: Bottom Sheet
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50"
                        >
                            <motion.div
                                ref={dropdownRef}
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                className="bg-white w-full rounded-t-2xl shadow-2xl max-h-[70vh] flex flex-col overflow-hidden"
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        e.stopPropagation();
                                        setIsOpen(false);
                                        triggerRef.current?.focus();
                                    }
                                }}
                            >
                                <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
                                    <h3 className="font-bold text-gray-700">{label || placeholderText}</h3>
                                    <button onClick={() => setIsOpen(false)} className="p-1 rounded-full bg-gray-200 text-gray-500" aria-label={t('modals.close')}>
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="overflow-y-auto p-2">
                                    {/* Simple Options */}
                                    {options.length > 0 && options.map((opt) => (
                                        <SelectItem
                                            key={opt.value}
                                            option={opt}
                                            isSelected={valueArray.includes(String(opt.value))}
                                            onSelect={handleSelect}
                                            mobile
                                        />
                                    ))}

                                    {/* Grouped Options */}
                                    {groups.map((group, idx) => (
                                        <div key={idx} className="mb-2">
                                            <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-50 rounded-lg mb-1">
                                                {group.label}
                                            </div>
                                            {group.options.map((opt) => (
                                                <SelectItem
                                                    key={opt.value}
                                                    option={opt}
                                                    isSelected={valueArray.includes(String(opt.value))}
                                                    onSelect={handleSelect}
                                                    mobile
                                                />
                                            ))}
                                        </div>
                                    ))}

                                    {allOptions.length === 0 && (
                                        <div className="p-8 text-center text-gray-500">
                                            {t('components.noOptions')}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : (
                        // Desktop: Floating Portal
                        <motion.div
                            ref={dropdownRef}
                            initial={{ opacity: 0, y: -5, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -5, scale: 0.98 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                            style={{
                                position: 'fixed',
                                top: dropdownPos.top,
                                bottom: dropdownPos.bottom,
                                left: dropdownPos.left,
                                width: dropdownPos.width,
                                zIndex: 9999
                            }}
                            className="bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto overflow-x-hidden"
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    triggerRef.current?.focus();
                                }
                            }}
                        >
                            {/* Simple Options */}
                            {options.length > 0 && (
                                <div className="p-1">
                                    {options.map((opt) => (
                                        <SelectItem
                                            key={opt.value}
                                            option={opt}
                                            isSelected={valueArray.includes(String(opt.value))}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Grouped Options */}
                            {groups.map((group, idx) => (
                                <div key={idx} className="p-1">
                                    <div className="px-2 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider bg-gray-50/50 rounded-md mb-1 mx-1">
                                        {group.label}
                                    </div>
                                    {group.options.map((opt) => (
                                        <SelectItem
                                            key={opt.value}
                                            option={opt}
                                            isSelected={valueArray.includes(String(opt.value))}
                                            onSelect={handleSelect}
                                        />
                                    ))}
                                </div>
                            ))}

                            {allOptions.length === 0 && (
                                <div className="p-4 text-center text-sm text-gray-500">
                                    {t('components.noOptions')}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

// Helper Sub-Component for individual items
const SelectItem: React.FC<{
    option: Option;
    isSelected: boolean;
    onSelect: (val: string | number) => void;
    mobile?: boolean;
}> = ({ option, isSelected, onSelect, mobile }) => {
    return (
        <button
            type="button"
            onClick={() => onSelect(option.value)}
            onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = (e.currentTarget.nextElementSibling || e.currentTarget.parentElement?.nextElementSibling?.querySelector('button')) as HTMLButtonElement;
                    if (next) next.focus();
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = (e.currentTarget.previousElementSibling || e.currentTarget.parentElement?.previousElementSibling?.querySelector('button:last-of-type')) as HTMLButtonElement;
                    if (prev) prev.focus();
                }
            }}
            className={cn(
                "w-full text-left rounded-lg transition-colors flex items-center justify-between group focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 focus-visible:outline-none",
                mobile ? "p-4 border-b border-gray-50 last:border-0" : "px-3 py-2 text-sm",
                isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
            )}
        >
            <div className="flex items-center gap-3 min-w-0">
                {option.icon && (
                    <span className={cn(isSelected ? "text-blue-500" : "text-gray-400 group-hover:text-gray-600")}>
                        {option.icon}
                    </span>
                )}
                <div className="flex flex-col min-w-0">
                    <span className={cn("truncate", isSelected ? "font-bold" : "font-normal", mobile && "text-base")}>
                        {option.label}
                    </span>
                    {option.description && (
                        <span className={cn("truncate", isSelected ? "text-blue-500/80" : "text-gray-400", mobile ? "text-sm" : "text-xs")}>
                            {option.description}
                        </span>
                    )}
                </div>
            </div>
            {isSelected && <CheckCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />}
        </button>
    );
};

export default CustomSelect;
